import Vue from 'vue';
import Vuex from 'vuex';
import ApiService from '../services/api.service';

/*
==============================!!!!IMPORTANTE!!!!===============================
Para adicionar novos campos no dashboard é preciso atualizar em 2 lugares:
  - state
  - getters
==============================------------------===============================
Para adicionar uma nova url é preciso atualizar em 1 lugar:
  - App.vue -> script; methods; switchContext(); fazer o setter
==============================------------------===============================
Para adicionar uma nova aba no admin é preciso atualizar em 6 lugares:
  - App.vue -> script; adicionar na constante CONTEXTS o nome da aba nova
  - App.vue -> script; importar o componente
  - App.vue -> script; registrar o componente
  - App.vue -> script; computed; pegar o getter que diz que foi carregado o conteúdo
  - App.vue -> script; methods; switchContext(); fazer o setter
  - App.vue -> template; colocar o componente no html com seu "code"

------------->
  O index dos filtros é armazenado aqui porque quando o vue "des-renderiza" o
  filtro ele perde sua última escolha
                                                                <--------------
*/

Vue.use(Vuex);

// requests ===================================================================
function callApiService(url, payload = {}) {
  return new Promise((resolve) => {
    let custom_payload = payload || {};
    custom_payload.t = new Date().getTime();
    ApiService.get(url, { params: custom_payload }).then(response => {
      resolve(response.data);
    });
  });
}

// async resolvers ============================================================
async function getContext(url, payload) {
  return await callApiService(url, payload);
}

// general functions ==========================================================
let isRequesting = false;

function toogleRequesting(state) {
  isRequesting = !isRequesting;
  state.isRequesting = isRequesting;
}

function parseNumericValue(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return 0;
    }

    const withoutPercent = trimmed.endsWith('%')
      ? trimmed.slice(0, -1).trim()
      : trimmed.replace(/%/g, '').trim();

    const hasComma = trimmed.includes(',');
    const normalized = hasComma
      ? withoutPercent.replace(/\./g, '').replace(',', '.')
      : withoutPercent;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function normalizePercentageNumber(value) {
  const numeric = parseNumericValue(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function hasPositivePercentageValue(value) {
  return normalizePercentageNumber(value) > 0;
}

function isZeroPercentageValue(value) {
  return normalizePercentageNumber(value) === 0;
}

function parseOptionalNumericValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return parseNumericValue(value);
}

function parseIntegerValue(value) {
  return Math.round(parseNumericValue(value));
}

function formatCurrencyBRL(value) {
  const numeric = parseNumericValue(value);
  return `R$ ${numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDisplayValue(value) {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  return `${value}`;
}

function sumPaymentValues(val1, val2) {
  const num1 = parseNumericValue(val1);
  const num2 = parseNumericValue(val2);
  return num1 + num2;
}

function sumPaymentPercentages(val1, val2) {
  const num1 = parseNumericValue(val1);
  const num2 = parseNumericValue(val2);
  const sum = num1 + num2;
  if (sum === 0) return '0%';
  return formatPercentage(sum);
}

function formatIntegerBR(value) {
  return parseIntegerValue(value).toLocaleString('pt-BR');
}

function formatPercentage(value) {
  const numeric = parseNumericValue(value);
  const fractionDigits = numeric % 1 === 0 ? 0 : 2;

  return `${numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2
  })}%`;
}

function coalesce() {
  for (let i = 0; i < arguments.length; i += 1) {
    const value = arguments[i];
    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return null;
}

function mapFunnelLikeResponseToState(state, data = {}, isFunnelContext = false) {
  const funil = data.funil || {};

  // FUNIL: Usar APENAS visitantes do Google Analytics (funnelAnalyticsVisits)
  // OUTROS CONTEXTOS: Usar customer_behavior_total_visits do backend
  let totalVisitsSource;
  
  if (isFunnelContext) {
    // FUNIL DE VENDAS: Visitantes vêm EXCLUSIVAMENTE do Google Analytics
    // Busca feita em updateFunnelSalesContext e salva em funnelAnalyticsVisits
    // IGNORA completamente data.customer_behavior_total_visits (valor incorreto)
    totalVisitsSource = state.funnelAnalyticsVisits != null 
      ? state.funnelAnalyticsVisits 
      : null;
  } else {
    // COMPORTAMENTO DO CLIENTE e outros contextos
    totalVisitsSource = data.customer_behavior_total_visits != null
      ? data.customer_behavior_total_visits
      : null;
  }

  const totalVisitsNumber = parseIntegerValue(totalVisitsSource);
  const totalVisitsDisplay = formatDisplayValue(totalVisitsSource);

  const totalCartsNumber = parseIntegerValue(
    funil.carrinhos != null ? funil.carrinhos : data.total_carrinhos
  );
  const totalOrdersNumber = parseIntegerValue(
    funil.pedidos != null ? funil.pedidos : data.total_convertidos
  );

  const funilLogin = parseIntegerValue(funil.login);
  const funilFrete = parseIntegerValue(funil.frete);
  const funilPagamento = parseIntegerValue(funil.pagamento);

  // Calcular checkout como soma de login + frete + pagamento (ignorando valor do backend)
  const totalCheckoutNumber = funilLogin + funilFrete + funilPagamento;

  const visitsToCarts = totalVisitsNumber > 0 ? (totalCartsNumber / totalVisitsNumber) * 100 : 0;
  const cartsToCheckout = totalCartsNumber > 0 ? (totalCheckoutNumber / totalCartsNumber) * 100 : 0;
  const loginToFrete = funilLogin > 0 ? (funilFrete / funilLogin) * 100 : 0;
  const freteToPagamento = funilFrete > 0 ? (funilPagamento / funilFrete) * 100 : 0;

  const devicesData = Array.isArray(data.pie_data_devices)
    ? data.pie_data_devices
    : (Array.isArray(data.devices) ? data.devices : []);

  const normalizeDeviceName = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

  const devicesTotalValue = devicesData.reduce((sum, device) => {
    const deviceValue = parseNumericValue(coalesce(
      device.value,
      device.y,
      device.count,
      device.total,
      device.total_value,
      device.quantity
    ));
    return sum + deviceValue;
  }, 0);

  const devicePercentageSources = [
    data.device_percentages,
    data.devicePercentages,
    data.devices_percentages,
    data.devicesPercentages,
  ].filter(source => source && typeof source === 'object');

  const resolveDevicePercentage = (directKeys, fallbackNames) => {
    for (let i = 0; i < directKeys.length; i += 1) {
      const directValue = parseOptionalNumericValue(data[directKeys[i]]);
      if (directValue !== null) {
        return directValue;
      }
    }

    for (let i = 0; i < devicePercentageSources.length; i += 1) {
      const source = devicePercentageSources[i];
      const entries = Array.isArray(source) ? source : Object.entries(source);

      for (let j = 0; j < entries.length; j += 1) {
        const entry = entries[j];
        if (!entry) {
          continue;
        }

        const [key, value] = Array.isArray(entry) ? entry : [entry.name, entry.value];
        if (fallbackNames.includes(normalizeDeviceName(key))) {
          const parsed = parseOptionalNumericValue(value);
          if (parsed !== null) {
            return parsed;
          }
        }
      }
    }

    const deviceEntry = devicesData.find(device => {
      const candidates = [
        device.name,
        device.label,
        device.type,
        device.slug,
        device.code,
        device.device
      ].map(normalizeDeviceName);

      return candidates.some(candidate => fallbackNames.includes(candidate));
    });

    if (deviceEntry) {
      const entryPercentage = parseOptionalNumericValue(coalesce(
        deviceEntry.percentage,
        deviceEntry.percent,
        deviceEntry.percentual,
        deviceEntry.perc,
        deviceEntry.value_percent,
        deviceEntry.percentValue
      ));

      if (entryPercentage !== null) {
        return entryPercentage;
      }

      const entryValue = parseNumericValue(coalesce(
        deviceEntry.value,
        deviceEntry.y,
        deviceEntry.count,
        deviceEntry.total,
        deviceEntry.total_value,
        deviceEntry.quantity
      ));

      const entryTotal = parseNumericValue(coalesce(
        deviceEntry.total,
        deviceEntry.total_value,
        deviceEntry.totalCount,
        devicesTotalValue > 0 ? devicesTotalValue : null,
        totalVisitsNumber
      ));

      if (entryValue > 0 && entryTotal > 0) {
        return (entryValue / entryTotal) * 100;
      }
    }

    return null;
  };

  const desktopPercentage = resolveDevicePercentage(
    ['desktop_percentage', 'desktopPercentual', 'desktopPercent', 'desktopPercentage'],
    ['desktop', 'computador', 'web']
  );
  const mobilePercentage = resolveDevicePercentage(
    ['mobile_percentage', 'mobilePercentual', 'mobilePercent', 'mobilePercentage'],
    ['mobile', 'celular', 'smartphone']
  );

  const conversionRateValue = parseNumericValue(
    data.taxa_conversao != null ? data.taxa_conversao : data.conversion_rate
  );
  const abandonmentRateValue = parseNumericValue(
    data.taxa_abandono != null ? data.taxa_abandono : data.abandonment_rate
  );
  const averageTicketValue = parseNumericValue(
    data.ticket_medio != null ? data.ticket_medio : data.average_ticket
  );
  const totalRevenueValue = parseNumericValue(
    data.receita_total != null ? data.receita_total : data.total_revenue
  );
  const totalAbandonedValue = parseIntegerValue(
    data.total_abandonados != null ? data.total_abandonados : data.total_abandoned
  );

  state.funnelData = [
    { name: 'Usuários', y: totalVisitsNumber },
    { name: 'Carrinhos', y: totalCartsNumber },
    { name: 'Checkout', y: totalCheckoutNumber },
    { name: 'Pedidos', y: totalOrdersNumber }
  ];

  state.conversionSteps = [
    {
      name: 'Usuários',
      value: totalVisitsDisplay,
      conversion: '100%'
    },
    {
      name: 'Carrinhos',
      value: totalCartsNumber.toLocaleString('pt-BR'),
      conversion: formatPercentage(visitsToCarts)
    },
    {
      name: 'Checkout',
      value: totalCheckoutNumber.toLocaleString('pt-BR'),
      conversion: formatPercentage(cartsToCheckout)
    },
    {
      name: 'Pedidos',
      value: totalOrdersNumber.toLocaleString('pt-BR'),
      conversion: formatPercentage(conversionRateValue)
    }
  ];

  state.checkoutSubSteps = [
    {
      name: 'Login',
      value: funilLogin.toLocaleString('pt-BR'),
      conversion: '100%'
    },
    {
      name: 'Frete',
      value: funilFrete.toLocaleString('pt-BR'),
      conversion: formatPercentage(loginToFrete)
    },
    {
      name: 'Pagamento',
      value: funilPagamento.toLocaleString('pt-BR'),
      conversion: formatPercentage(freteToPagamento)
    }
  ];

  state.abandonmentRate = formatPercentage(abandonmentRateValue);
  state.averageAbandonmentTime = 'N/A';
  state.lostValue = formatCurrencyBRL(totalRevenueValue);
  state.recoverableValue = formatCurrencyBRL(totalRevenueValue * 0.3);

  state.totalVisits = totalVisitsDisplay;
  state.totalCarts = totalCartsNumber;
  state.totalCheckout = totalCheckoutNumber;
  state.totalConverted = totalOrdersNumber;
  state.totalAbandoned = totalAbandonedValue;
  state.conversionRate = conversionRateValue;
  state.abandonmentRateValue = abandonmentRateValue;
  state.averageTicket = averageTicketValue;
  state.totalRevenue = totalRevenueValue;
  state.cartTimeline = Array.isArray(data.carrinhos_timeline) ? data.carrinhos_timeline : [];
  state.pie_data_devices = devicesData;
  state.desktop_percentage = desktopPercentage !== null ? desktopPercentage : null;
  state.mobile_percentage = mobilePercentage !== null ? mobilePercentage : null;

  state.funilCarrinhos = totalCartsNumber;
  state.funilCheckout = totalCheckoutNumber;
  state.funilLogin = funilLogin;
  state.funilFrete = funilFrete;
  state.funilPagamento = funilPagamento;
  state.funilPedidos = totalOrdersNumber;
}

function normalizeTopProducts(products) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.map((product, index) => {
    const totalCarts = parseIntegerValue(
      coalesce(product.total_carrinhos, product.total_carts, product.totalCarrinhos, product.count, 0)
    );
    const totalQuantity = parseIntegerValue(
      coalesce(product.quantidade_total, product.total_quantity, product.totalQuantidade, product.quantity, 0)
    );
    const convertedCarts = parseIntegerValue(
      coalesce(product.carrinhos_convertidos, product.converted_carts, product.convertedCarts, 0)
    );
    const conversionRate = parseOptionalNumericValue(
      coalesce(product.taxa_conversao, product.conversion_rate, product.conversionRate)
    );

    return {
      id: coalesce(product.produto_id, product.product_id, product.id, index),
      name: coalesce(product.nome, product.name, product.product_name, 'Produto sem nome'),
      sku: coalesce(product.sku, product.SKU, null),
      totalCarts,
      totalQuantity,
      convertedCarts,
      conversionRate,
    };
  });
}

export default new Vuex.Store({
  state: {
    // simple vars ============================================================
    periodTextArray: ['hoje', 'ontem', '7 dias', '15 dias', 'mês atual',
      'mês passado', 'ano atual', '1 ano', 'Intervalo de Datas'
    ],
    // general ================================================================
    isRequesting: isRequesting,
    // common =================================================================
    current_site_id: null,
    is_multi_site_manager: false,
    site_or_order_origin: [],
    stock_site_origin: [],
    current_month_name: null,
    previous_month_name: null,
    past_previous_month_name: null,
    // urls ===================================================================
    url_order_paid_orders: null,
    url_order_open_orders: null,
    url_order_cancelled_orders: null,
    url_order_billet: null,
    url_order_billet_paid: null,
    url_order_billet_open: null,
    url_order_billet_cancelled: null,
    url_order_card: null,
    url_order_card_paid: null,
    url_order_card_open: null,
    url_order_card_cancelled: null,
    url_order_pix: null,
    url_order_pix_paid: null,
    url_order_pix_open: null,
    url_order_pix_cancelled: null,
    url_order_intermediate: null,
    url_order_intermediate_paid: null,
    url_order_intermediate_open: null,
    url_order_intermediate_cancelled: null,
    url_order_current_period: null,
    // period context =========================================================
    orderPeriodSelectedIndex: 0, // mudar tbm no Filters.vue
    orderOriginSelectedIndex: 0,
    orderPeriodSelectedCode: 0,
    orderOriginSelectedCode: 0,
    orderPeriodDateStart: 0,
    orderPeriodDateEnd: 0,
    // --- API
    // --- Pedidos ---
    period_context_loaded: false,
    user_has_dashboard_permission: false,
    is_marketplace: false,
    previous_date_querystring: null,
    originOrder: [{ 'code': 1, 'label': 'Sua loja' }, { 'code': 2, 'label': 'Marketplace' }],
    show_diff: false,
    count: null,
    diff_count: null,
    diff_count_percentage: null,
    dispatched_orders: null,
    total_billed_value: null,
    customer_bill: null,
    customer_bill_percentage: null,
    average_shipping_cost: null,
    total_bucket: null,
    total_paid_orders_value: null,
    total_open_orders_value: null,
    total_cancelled_orders_value: null,
    total_paid_orders_count: null,
    total_open_orders_count: null,
    total_cancelled_orders_count: null,
    billets_count: null,
    cards_count: null,
    pix_count: null,
    intermediate_count: null,
    billets_paid_percentage: null,
    billets_open_percentage: null,
    billets_cancelled_percentage: null,
    cards_paid_percentage: null,
    cards_open_percentage: null,
    cards_cancelled_percentage: null,
    pix_paid_percentage: null,
    pix_open_percentage: null,
    pix_cancelled_percentage: null,
    intermediate_paid_percentage: null,
    intermediate_open_percentage: null,
    intermediate_cancelled_percentage: null,
    others_count: null,
    others_paid_percentage: null,
    others_open_percentage: null,
    others_cancelled_percentage: null,
    valid_percentage: null,
    paid_percentage: null,
    open_percentage: null,
    total_orders: null,
    valid_orders: null,
    paid_orders: null,
    open_orders: null,
    avg_ticket: null,
    diff_avg_ticket: null,
    diff_avg_ticket_percentage: null,
    pie_data_states: [],
    pie_data_devices: [],
    pie_data_profiles_gender: [],
    pie_data_profiles_age: [],
    profile_drilldown_series: [],
    mobile_percentage: null,
    desktop_percentage: null,
    orders_new_customers: null,
    diff_orders_new_customers: null,
    diff_orders_new_customers_percentage: null,
    orders_recurrent_customers: null,
    diff_orders_recurrent_customers: null,
    diff_orders_recurrent_customers_percentage: null,
    // For procedural use in Analytics request
    site_id: null,
    current_total_orders_value_count: null,
    current_paid_orders_count: null,
    past_paid_orders_count: null,
    past_total_orders_value_count: null,

    // --- Clientes ---
    customers: null,
    diff_customers: null,
    diff_customers_percentage: null,

    // --- Utiliza Analytics - Pedidos ---
    analytics_loaded: null,
    total_visits: null,
    diff_total_visits: null,
    total_conversion_rate: null,
    diff_total_conversion_rate: null,
    paid_conversion_rate: null,
    diff_paid_conversion_rate: null,
    line_data_page_views: [],
    visits_page_views: [],
    diff_total_conversion_rate_percentage: null,
    diff_paid_conversion_rate_percentage: null,
    diff_total_visits_percentage: null,
    // status context =========================================================
    status_context_loaded: false,
    today_count: null,
    today_value: null,
    today_ticket: null,
    today_paid_count: null,
    today_paid_value: null,
    today_paid_ticket: null,
    today_open_count: null,
    today_open_value: null,
    today_open_ticket: null,
    today_cancelled_count: null,
    today_cancelled_value: null,
    today_cancelled_ticket: null,
    yesterday_count: null,
    yesterday_value: null,
    yesterday_ticket: null,
    yesterday_paid_count: null,
    yesterday_paid_value: null,
    yesterday_paid_ticket: null,
    yesterday_open_count: null,
    yesterday_open_value: null,
    yesterday_open_ticket: null,
    yesterday_cancelled_count: null,
    yesterday_cancelled_value: null,
    yesterday_cancelled_ticket: null,
    current_month_count: null,
    current_month_value: null,
    current_month_ticket: null,
    current_month_paid_count: null,
    current_month_paid_value: null,
    current_month_paid_ticket: null,
    current_month_open_count: null,
    current_month_open_value: null,
    current_month_open_ticket: null,
    current_month_cancelled_count: null,
    current_month_cancelled_value: null,
    current_month_cancelled_ticket: null,
    previous_month_count: null,
    previous_month_value: null,
    previous_month_ticket: null,
    previous_month_paid_count: null,
    previous_month_paid_value: null,
    previous_month_paid_ticket: null,
    previous_month_open_count: null,
    previous_month_open_value: null,
    previous_month_open_ticket: null,
    previous_month_cancelled_count: null,
    previous_month_cancelled_value: null,
    previous_month_cancelled_ticket: null,
    lifetime_count: null,
    lifetime_value: null,
    lifetime_ticket: null,
    lifetime_paid_count: null,
    lifetime_paid_value: null,
    lifetime_paid_ticket: null,
    lifetime_open_count: null,
    lifetime_open_value: null,
    lifetime_open_ticket: null,
    lifetime_cancelled_count: null,
    lifetime_cancelled_value: null,
    lifetime_cancelled_ticket: null,
    // sales_products context =================================================
    productsSiteOriginSelectedIndex: 0,
    salesProductsSiteOriginSelectedIndex: 0,
    salesProductsOriginSelectedCode: 0,
    // --- API
    sales_products_context_loaded: false,
    past_month_scalable_orders_value: [],
    current_scalable_orders_value: [],
    past_month_daily_sales: [],
    current_daily_sales: [],
    monthly_sales_by_year: [],
    monthly_sales_qtd: {},
    channels_data: [],
    drilldown_series: [],
    previous_items: [],
    current_items: [],
    // bars context ===========================================================
    receipt_visits_conversion_context_loaded: false,
    semester_conversion: [],
    semester_bucket: [],
    semester_visits: [],
    bars_categories: [],
    // stock context ==========================================================
    stockSiteOriginSelectedIndex: 0,
    stockOriginSelectedCode: 0,
    // --- API
    stock_context_loaded: false,
    stock: null,
    stock_sku: null,
    minimum_stock_sku: null,
    no_stock_sku: null,
    minimum_stock_sku_products: null,
    no_stock_sku_products: null,
    stock_max_limit: null,
    url_product_sku_minimum_stock: null,
    url_product_sku_no_stock: null,
    url_product_sku_with_stock: null,
    url_product: null,
    // cancelled context ======================================================
    cancelled_context_loaded: false,
    total_cancelled_count: null,
    cancelled_sum_0: null,
    cancelled_sum_1: null,
    cancelled_sum_2: null,
    cancelled_sum_3: null,
    cancelled_sum_4: null,
    cancelled_sum_5: null,
    cancelled_len_0: null,
    cancelled_len_1: null,
    cancelled_len_2: null,
    cancelled_len_3: null,
    cancelled_len_4: null,
    cancelled_len_5: null,
    pie_data_cancelled_values: [],
    cancelled_methods_data_0: [],
    cancelled_methods_drilldown_series_0: [],
    cancelled_methods_data_1: [],
    cancelled_methods_data_2: [],
    cancelled_methods_data_3: [],
    cancelled_methods_data_4: [],
    cancelled_methods_drilldown_series_4: [],
    cancelled_methods_data_5: [],
    cancelled_methods_drilldown_series_5: [],
    dissatisfaction_count: null,
    dissatisfaction_data: [],
    // ticket progression context =============================================
    ticket_progression_context_loaded: false,
    daily_sales: [],
    min_ticket: null,
    min_date: null,
    max_ticket: null,
    max_date: null,
    average_total_daily_ticket: null,
    // abc curve context ======================================================
    abcPeriodSelectedIndex: 7, // mudar tbm no Filters.vue
    abcPeriodSelectedCode: 7,
    abcAdminPeriod: 9, // esse code tem que bater com o filtro de datas do admin do django

    // --- API
    abc_curve_context_loaded: false,
    class_a_products: [],
    class_b_products: [],
    class_c_products: [],
    class_a_resume: null,
    class_b_resume: null,
    class_c_resume: null,
    abc_graph_data_a: [],
    abc_graph_data_b: [],
    abc_graph_data_c: [],
    item_mark_a: [],
    item_mark_b: [],
    item_mark_c: [],
    // shipping costs context ======================================================
    shipping_costs_context_loaded: false,
    shipping_initial_costs: [],
    shipping_final_costs: [],
    shipping_difference_costs: [],
    // funnel sales context ========================================================
    funnel_sales_context_loaded: false,
    funnelData: [],
    conversionSteps: [],
    checkoutSubSteps: [],
    funnelPeriodSelectedIndex: 0,
    funnelOriginSelectedIndex: 0,
    funnelPeriodSelectedCode: 0,
    funnelOriginSelectedCode: 0,
    funnelPeriodDateStart: 0,
    funnelPeriodDateEnd: 0,
    // Dados adicionais do funil
    funnelAnalyticsVisits: null, // Visitantes específicos do funil (não compartilhados)
    totalVisits: null,
    totalCarts: null,
    totalCheckout: null,
    totalConverted: null,
    totalAbandoned: null,
    conversionRate: null,
    abandonmentRateValue: null,
    averageTicket: null,
    totalRevenue: null,
    cartTimeline: [],
    // Dados específicos do funil detalhado
    funilUsuarios: null,
    funilCarrinhos: null,
    funilCheckout: null,
    funilLogin: null,
    funilFrete: null,
    funilPagamento: null,
    funilPedidos: null,
    // live cart context ===========================================================
    live_cart_context_loaded: false,
    activeCarts: [],
    realTimeUpdates: [],
    abandonedCartData: [],
    abandonmentRate: null,
    averageAbandonmentTime: null,
    lostValue: null,
    recoverableValue: null,
    liveCartPeriodSelectedIndex: 0,
    liveCartOriginSelectedIndex: 0,
    liveCartPeriodSelectedCode: 0,
    liveCartOriginSelectedCode: 0,
    liveCartPeriodDateStart: 0,
    liveCartPeriodDateEnd: 0,
    // customer behavior context ===========================================================
    customer_behavior_context_loaded: false,
    // sales map context ======================================================
    sales_map_context_loaded: false,
    sales_map_title: '',
    sales_map_total_orders: 0,
    sales_map_applied_filters: [],
    sales_map_regions: [],
    sales_map_customer_profile: {
      males: { quantity: 0, percentage: 0 },
      females: { quantity: 0, percentage: 0 },
      ageGroups: []
    },
    sales_map_top_shipping_services: [],
    sales_map_top_shipping_title: 'Top Serviços de Entrega',
    sales_map_top_products: [],
    sales_map_top_products_title: 'Top Produtos',
    sales_map_top_cities: [],
    sales_map_top_cities_title: 'Top Cidades',
    sales_map_top_states: [],
    sales_map_top_states_title: 'Top 10 Estados',
    sales_map_state_values: {},
    sales_map_svg_content: '',
    sales_map_states_details: {},
    topProductsAdded: [],
    customerBehaviorPeriodSelectedIndex: 0,
    customerBehaviorOriginSelectedIndex: 0,
    customerBehaviorPeriodSelectedCode: 0,
    customerBehaviorOriginSelectedCode: 0,
    customerBehaviorPeriodDateStart: 0,
    customerBehaviorPeriodDateEnd: 0
  },
  getters: {
    // simple vars ============================================================
    periodTextArray(state) { return state.periodTextArray; },
    // components comm ========================================================
    orderPeriodSelectedCode(state) { return state.orderPeriodSelectedCode; },
    orderOriginSelectedCode(state) { return state.orderOriginSelectedCode; },
    orderPeriodDateStart(state) { return state.orderPeriodDateStart; },
    orderPeriodDateEnd(state) { return state.orderPeriodDateEnd; },
    stockOriginSelectedCode(state) { return state.stockOriginSelectedCode; },
    salesProductsOriginSelectedCode(state) { return state.salesProductsOriginSelectedCode; },
    orderPeriodSelectedIndex(state) { return state.orderPeriodSelectedIndex; },
    orderOriginSelectedIndex(state) { return state.orderOriginSelectedIndex; },
    productsSiteOriginSelectedIndex(state) { return state.productsSiteOriginSelectedIndex; },
    salesProductsSiteOriginSelectedIndex(state) { return state.salesProductsSiteOriginSelectedIndex; },
    stockSiteOriginSelectedIndex(state) { return state.stockSiteOriginSelectedIndex; },
    abcAdminPeriod(state) { return state.abcAdminPeriod; },
    // general ================================================================
    isRequesting(state) { return state.isRequesting; },
    // common =================================================================
    is_multi_site_manager(state) { return state.is_multi_site_manager; },
    site_or_order_origin(state) { return state.site_or_order_origin; },
    stock_site_origin(state) { return state.stock_site_origin; },
    current_month_name(state) { return state.current_month_name; },
    previous_month_name(state) { return state.previous_month_name; },
    past_previous_month_name(state) { return state.past_previous_month_name; },
    baseUrl() {
      let urlParams = new URLSearchParams(window.location.search);
      let jwtToken = urlParams.get('token');

      let tokenParts = jwtToken.split('.');
      let decodedData = JSON.parse(window.atob(tokenParts[1]));
      let debug = decodedData.debug;  // O debug é coletado do backend

      let baseUrl;
      if (debug) {
        // Forçar localhost em desenvolvimento
        baseUrl = "http://localhost:8000";
      } else {
        baseUrl = "https://" + decodedData.domain;
      }

      return baseUrl;
    },
    // urls ===================================================================
    url_order_current_period(state) { return state.url_order_current_period; },
    // period context =========================================================
    // --- Pedidos ---

    total_orders_count(state) { return state.total_orders },
    total_order_paid(state) { return state.total_paid_orders_value },
    total_paid_orders_count(state) { return state.total_paid_orders_count },
    url_order_paid_orders(state) { return state.url_order_paid_orders },
    PeriodCardsObject_1(state) {
      let showDiff = state.show_diff;
      return [{
        'info': state.total_visits,
        'show_diff': showDiff,
        'diff': state.diff_total_visits_percentage,
        'diff_qtde': state.diff_total_visits,
        'bg': 'rb',
        'img': 'period/visitors.svg',
        'title': 'visitantes',
        'condition': (!state.is_multi_site_manager && !state.is_marketplace) ||
          (state.is_multi_site_manager && !state.is_marketplace && state.site_id),
        'extra_info': false
      }, {
        'info': state.orders_new_customers + ' pedidos',
        'show_diff': showDiff,
        'diff': state.diff_orders_new_customers_percentage,
        'diff_qtde': state.diff_orders_new_customers,
        'bg': 'yb',
        'img': 'period/new_customers_orders.svg',
        'title': 'de clientes novos',
        'condition': true,
        'extra_info': false
      }, {
        'info': state.count,
        'show_diff': showDiff,
        'diff': state.diff_count_percentage,
        'diff_qtde': state.diff_count,
        'bg': 'gb',
        'img': 'period/orders.svg',
        'title': 'pedidos realizados',
        'condition': true,
        'extra_info': false
      }, {
        'info': state.avg_ticket,
        'show_diff': showDiff,
        'diff': state.diff_avg_ticket_percentage,
        'diff_qtde': state.diff_avg_ticket,
        'bg': 'bb',
        'img': 'ticket.svg',
        'title': 'ticket médio',
        'condition': true,
        'extra_info': false
      }];
    },
    PeriodCardsObject_2(state) {
      let showDiff = state.show_diff;
      return [{
        'info': state.customers,
        'show_diff': showDiff,
        'diff': state.diff_customers_percentage,
        'diff_qtde': state.diff_customers,
        'bg': 'rb',
        'img': 'period/new_customers.svg',
        'title': 'novos clientes cadastrados',
        'condition': true,
        'extra_info': false
      }, {
        'info': state.orders_recurrent_customers + ' pedidos',
        'show_diff': showDiff,
        'diff': state.diff_orders_recurrent_customers_percentage,
        'diff_qtde': state.diff_orders_recurrent_customers,
        'bg': 'yb',
        'img': 'period/recurrent_customers.svg',
        'title': 'de clientes recorrentes',
        'condition': true,
        'extra_info': false
      }, {
        'info': state.total_conversion_rate,
        'show_diff': showDiff,
        'diff': state.diff_total_conversion_rate_percentage,
        'diff_qtde': state.diff_total_conversion_rate,
        'bg': 'gb',
        'img': 'period/conversion_rate.svg',
        'title': 'taxa de conversão',
        'condition': (!state.is_multi_site_manager && !state.is_marketplace) ||
          (state.is_multi_site_manager && !state.is_marketplace && state.site_id),
        'extra_info': '(sem contabilizar marketplace)'
      }, {
        'info': state.paid_conversion_rate,
        'show_diff': showDiff,
        'diff': state.diff_paid_conversion_rate_percentage,
        'diff_qtde': state.diff_paid_conversion_rate,
        'bg': 'bb',
        'img': 'period/real_conversion_rate.svg',
        'title': 'taxa de conversão real',
        'condition': (!state.is_multi_site_manager && !state.is_marketplace) ||
          (state.is_multi_site_manager && !state.is_marketplace && state.site_id),
        'extra_info': '(sem contabilizar marketplace)'
      }];
    },
    PaymentsInfoObject_1(state) {
      return [{
        'bg': 'yb',
        'info': state.total_open_orders_value,
        'url': state.url_order_open_orders,
        'label': 'em aberto',
        'count': state.total_open_orders_count
      }, {
        'bg': 'rb',
        'info': state.total_cancelled_orders_value,
        'url': state.url_order_cancelled_orders,
        'label': 'cancelados',
        'count': state.total_cancelled_orders_count
      }];
    },
    PaymentsInfoObject_2(state) {
      return [{
        'info': state.billets_count,
        'url': state.url_order_billet,
        'url_label': 'boleto',
        'img': 'period/billet_icon.svg',
        'array': [{
          'bg': 'gb',
          'info': state.billets_paid_percentage + ' pagos',
          'url': state.url_order_billet_paid
        }, {
          'bg': 'yb',
          'info': state.billets_open_percentage + ' abertos',
          'url': state.url_order_billet_open
        }, {
          'bg': 'rb',
          'info': state.billets_cancelled_percentage + ' cancelados',
          'url': state.url_order_billet_cancelled
        }]
      }, {
        'info': state.cards_count,
        'url': state.url_order_card,
        'url_label': 'cartão',
        'img': 'period/cart_icon.svg',
        'array': [{
          'bg': 'gb',
          'info': state.cards_paid_percentage + ' pagos',
          'url': state.url_order_card_paid
        }, {
          'bg': 'yb',
          'info': state.cards_open_percentage + ' abertos',
          'url': state.url_order_card_open
        }, {
          'bg': 'rb',
          'info': state.cards_cancelled_percentage + ' cancelados',
          'url': state.url_order_card_cancelled
        }]
      }, {
        'info': state.pix_count,
        'url': state.url_order_pix,
        'url_label': 'Pix',
        'img': 'period/pix_icon.svg',
        'array': [{
          'bg': 'gb',
          'info': state.pix_paid_percentage + ' pagos',
          'url': state.url_order_pix_paid
        }, {
          'bg': 'yb',
          'info': state.pix_open_percentage + ' abertos',
          'url': state.url_order_pix_open
        }, {
          'bg': 'rb',
          'info': state.pix_cancelled_percentage + ' cancelados',
          'url': state.url_order_pix_cancelled
        }]
      }, {
        'info': sumPaymentValues(state.intermediate_count, state.others_count),
        'url': state.url_order_intermediate,
        'url_label': 'Outros',
        'img': 'period/total_bucket_card.svg',
        'array': [{
          'bg': 'gb',
          'info': sumPaymentPercentages(state.intermediate_paid_percentage, state.others_paid_percentage) + ' pagos',
          'url': state.url_order_intermediate_paid
        }, {
          'bg': 'yb',
          'info': sumPaymentPercentages(state.intermediate_open_percentage, state.others_open_percentage) + ' abertos',
          'url': state.url_order_intermediate_open
        }, {
          'bg': 'rb',
          'info': sumPaymentPercentages(state.intermediate_cancelled_percentage, state.others_cancelled_percentage) + ' cancelados',
          'url': state.url_order_intermediate_cancelled
        }]
      }];
    },
    ShippingsCardsObject(state) {
      return [{
        'bg': 'gb',
        'info': state.dispatched_orders,
        'img': 'shippings/dispatched_orders.svg',
        'title': 'pedidos despachados'
      }, {
        'bg': 'yb',
        'info': state.total_billed_value,
        'img': 'shippings/billed_value.svg',
        'title': 'valor faturado'
      }, {
        'bg': 'rb',
        'info': state.customer_bill + ' (' + state.customer_bill_percentage + ')',
        'img': 'shippings/customer_shipping_paid.svg',
        'title': 'frete pago pelo cliente'
      }, {
        'bg': 'bb',
        'info': state.average_shipping_cost,
        'img': 'ticket.svg',
        'title': 'valor médio de frete'
      }];
    },
    period_context_loaded(state) { return state.period_context_loaded; },
    user_has_dashboard_permission(state) { return state.user_has_dashboard_permission; },
    is_marketplace(state) { return state.is_marketplace; },
    previous_date_querystring(state) { return state.previous_date_querystring; },
    originOrder(state) { return state.originOrder; },
    total_bucket(state) { return state.total_bucket; },
    pie_data_states(state) { return state.pie_data_states; },
    pie_data_devices(state) { return state.pie_data_devices; },
    pie_data_profiles_gender(state) { return state.pie_data_profiles_gender; },
    pie_data_profiles_age(state) { return state.pie_data_profiles_age; },
    profile_drilldown_series(state) { return state.profile_drilldown_series; },
    mobile_percentage(state) { return state.mobile_percentage; },
    desktop_percentage(state) { return state.desktop_percentage; },
    // For procedural use in Analytics request
    site_id(state) { return state.site_id; },
    current_total_orders_value_count(state) { return state.current_total_orders_value_count; },
    current_paid_orders_count(state) { return state.current_paid_orders_count; },
    past_paid_orders_count(state) { return state.past_paid_orders_count; },
    past_total_orders_value_count(state) { return state.past_total_orders_value_count; },
    // --- Utiliza Analytics - Pedidos ---
    analytics_loaded(state) { return state.analytics_loaded; },
    line_data_page_views(state) { return state.line_data_page_views; },
    visits_page_views(state) { return state.visits_page_views; },
    // status context =====================================================
    status_context_loaded(state) { return state.status_context_loaded; },
    StatusInfoObject(state) {
      return [
        ['Todos os pedidos', [{
          'has_style': true,
          'title': 'Hoje',
          'qtd': state.today_count,
          'value': state.today_value,
          'ticket': state.today_ticket
        }, {
          'has_style': false,
          'title': 'Ontem',
          'qtd': state.yesterday_count,
          'value': state.yesterday_value,
          'ticket': state.yesterday_ticket
        }, {
          'has_style': false,
          'title': 'Mês atual',
          'qtd': state.current_month_count,
          'value': state.current_month_value,
          'ticket': state.current_month_ticket
        }, {
          'has_style': false,
          'title': 'Mês anterior',
          'qtd': state.previous_month_count,
          'value': state.previous_month_value,
          'ticket': state.previous_month_ticket
        }, {
          'has_style': false,
          'title': 'Lifetime',
          'qtd': state.lifetime_count,
          'value': state.lifetime_value,
          'ticket': state.lifetime_ticket
        }, ]],
        ['Pedidos pagos', [{
          'has_style': true,
          'title': 'Hoje',
          'qtd': state.today_paid_count,
          'value': state.today_paid_value,
          'ticket': state.today_paid_ticket
        }, {
          'has_style': false,
          'title': 'Ontem',
          'qtd': state.yesterday_paid_count,
          'value': state.yesterday_paid_value,
          'ticket': state.yesterday_paid_ticket
        }, {
          'has_style': false,
          'title': 'Mês atual',
          'qtd': state.current_month_paid_count,
          'value': state.current_month_paid_value,
          'ticket': state.current_month_paid_ticket
        }, {
          'has_style': false,
          'title': 'Mês anterior',
          'qtd': state.previous_month_paid_count,
          'value': state.previous_month_paid_value,
          'ticket': state.previous_month_paid_ticket
        }, {
          'has_style': false,
          'title': 'Lifetime',
          'qtd': state.lifetime_paid_count,
          'value': state.lifetime_paid_value,
          'ticket': state.lifetime_paid_ticket
        }, ]],
        ['Pedidos em aberto', [{
          'has_style': true,
          'title': 'Hoje',
          'qtd': state.today_open_count,
          'value': state.today_open_value,
          'ticket': state.today_open_ticket
        }, {
          'has_style': false,
          'title': 'Ontem',
          'qtd': state.yesterday_open_count,
          'value': state.yesterday_open_value,
          'ticket': state.yesterday_open_ticket
        }, {
          'has_style': false,
          'title': 'Mês atual',
          'qtd': state.current_month_open_count,
          'value': state.current_month_open_value,
          'ticket': state.current_month_open_ticket
        }, {
          'has_style': false,
          'title': 'Mês anterior',
          'qtd': state.previous_month_open_count,
          'value': state.previous_month_open_value,
          'ticket': state.previous_month_open_ticket
        }, {
          'has_style': false,
          'title': 'Lifetime',
          'qtd': state.lifetime_open_count,
          'value': state.lifetime_open_value,
          'ticket': state.lifetime_open_ticket
        }, ]],
        ['Pedidos cancelados', [{
          'has_style': true,
          'title': 'Hoje',
          'qtd': state.today_cancelled_count,
          'value': state.today_cancelled_value,
          'ticket': state.today_cancelled_ticket
        }, {
          'has_style': false,
          'title': 'Ontem',
          'qtd': state.yesterday_cancelled_count,
          'value': state.yesterday_cancelled_value,
          'ticket': state.yesterday_cancelled_ticket
        }, {
          'has_style': false,
          'title': 'Mês atual',
          'qtd': state.current_month_cancelled_count,
          'value': state.current_month_cancelled_value,
          'ticket': state.current_month_cancelled_ticket
        }, {
          'has_style': false,
          'title': 'Mês anterior',
          'qtd': state.previous_month_cancelled_count,
          'value': state.previous_month_cancelled_value,
          'ticket': state.previous_month_cancelled_ticket
        }, {
          'has_style': false,
          'title': 'Lifetime',
          'qtd': state.lifetime_cancelled_count,
          'value': state.lifetime_cancelled_value,
          'ticket': state.lifetime_cancelled_ticket
        }, ]]
      ];
    },
    // sales_products context ======================================================
    sales_products_context_loaded(state) { return state.sales_products_context_loaded; },
    past_month_scalable_orders_value(state) { return state.past_month_scalable_orders_value; },
    current_scalable_orders_value(state) { return state.current_scalable_orders_value; },
    past_month_daily_sales(state) { return state.past_month_daily_sales; },
    current_daily_sales(state) { return state.current_daily_sales; },
    years_keys(state) { return state.years_keys; },
    monthly_sales_by_year(state) { return state.monthly_sales_by_year; },
    monthly_sales_qtd(state) { return state.monthly_sales_qtd; },
    channels_data(state) { return state.channels_data; },
    drilldown_series(state) { return state.drilldown_series; }, // highcharts
    previous_items(state) { return state.previous_items; },
    current_items(state) { return state.current_items; },
    // bars context =======================================================
    receipt_visits_conversion_context_loaded(state) { return state.receipt_visits_conversion_context_loaded; },
    semester_conversion(state) { return state.semester_conversion; },
    semester_bucket(state) { return state.semester_bucket; },
    semester_visits(state) { return state.semester_visits; },
    bars_categories(state) { return state.bars_categories; },
    // stock context ======================================================
    stock_context_loaded(state) { return state.stock_context_loaded; },
    StockInfoObject(state) {
      return [{
        'bg': 'gb',
        'info': state.stock,
        'img': 'stock/items.svg',
        'title': 'itens em estoque',
        'url': state.url_product
      }, {
        'bg': 'bb',
        'info': state.stock_sku,
        'img': 'stock/skus.svg',
        'title': 'skus com estoque',
        'url': state.url_product_sku_with_stock
      }, {
        'bg': 'yb',
        'info': state.minimum_stock_sku,
        'img': 'stock/minimum_stock.svg',
        'title': 'skus em estoque mínimo',
        'url': state.url_product_sku_minimum_stock
      }, {
        'bg': 'rb',
        'info': state.no_stock_sku,
        'img': 'stock/no_stock.svg',
        'title': 'skus sem estoque',
        'url': state.url_product_sku_no_stock
      }];
    },
    minimum_stock_sku_products(state) { return state.minimum_stock_sku_products; },
    no_stock_sku_products(state) { return state.no_stock_sku_products; },
    stock_max_limit(state) { return state.stock_max_limit; },
    // cancelled context ==================================================
    cancelled_context_loaded(state) { return state.cancelled_context_loaded; },
    cancelled_sum_0(state) { return state.cancelled_sum_0; },
    total_cancelled_count(state) { return state.total_cancelled_count; },
    cancelled_sum_1(state) { return state.cancelled_sum_1; },
    cancelled_sum_2(state) { return state.cancelled_sum_2; },
    cancelled_sum_3(state) { return state.cancelled_sum_3; },
    cancelled_sum_4(state) { return state.cancelled_sum_4; },
    cancelled_sum_5(state) { return state.cancelled_sum_5; },
    cancelled_len_0(state) { return state.cancelled_len_0; },
    cancelled_len_1(state) { return state.cancelled_len_1; },
    cancelled_len_2(state) { return state.cancelled_len_2; },
    cancelled_len_3(state) { return state.cancelled_len_3; },
    cancelled_len_4(state) { return state.cancelled_len_4; },
    cancelled_len_5(state) { return state.cancelled_len_5; },
    pie_data_cancelled_values(state) { return state.pie_data_cancelled_values; },
    cancelled_methods_data_0(state) { return state.cancelled_methods_data_0; },
    cancelled_methods_drilldown_series_0(state) { return state.cancelled_methods_drilldown_series_0; },
    cancelled_methods_data_1(state) { return state.cancelled_methods_data_1; },
    cancelled_methods_data_2(state) { return state.cancelled_methods_data_2; },
    cancelled_methods_data_3(state) { return state.cancelled_methods_data_3; },
    cancelled_methods_data_4(state) { return state.cancelled_methods_data_4; },
    cancelled_methods_drilldown_series_4(state) { return state.cancelled_methods_drilldown_series_4; },
    cancelled_methods_data_5(state) { return state.cancelled_methods_data_5; },
    cancelled_methods_drilldown_series_5(state) { return state.cancelled_methods_drilldown_series_5; },
    dissatisfaction_count(state) { return state.dissatisfaction_count; },
    dissatisfaction_data(state) { return state.dissatisfaction_data; },
    // ticket progression context =========================================
    ticket_progression_context_loaded(state) { return state.ticket_progression_context_loaded; },
    TicketProgressionObject(state) {
      return [{
        'info': state.average_total_daily_ticket,
        'title': 'média dos dias',
        'bg': 'yb',
        'img': 'ticket_progression/avg_ticket_progression.svg'
      }, {
        'info': state.min_ticket,
        'title': 'menor ticket médio real (' + state.min_date + ')',
        'bg': 'bb',
        'img': 'ticket_progression/min_ticket.svg'
      }, {
        'info': state.max_ticket,
        'title': 'maior ticket médio real (' + state.max_date + ')',
        'bg': 'gb',
        'img': 'ticket_progression/max_ticket.svg'
      }];
    },
    daily_sales(state) { return state.daily_sales; },
    min_ticket(state) { return state.min_ticket; },
    min_date(state) { return state.min_date; },
    max_ticket(state) { return state.max_ticket; },
    max_date(state) { return state.max_date; },
    average_total_daily_ticket(state) { return state.average_total_daily_ticket; },
    // abc curve context ======================================================
    abcPeriodSelectedIndex(state) { return state.abcPeriodSelectedIndex; },
    abcPeriodSelectedCode(state) { return state.abcPeriodSelectedCode; },
    abc_curve_context_loaded(state) { return state.abc_curve_context_loaded; },
    abc_graph_data_a(state) { return state.abc_graph_data_a; },
    abc_graph_data_b(state) { return state.abc_graph_data_b; },
    abc_graph_data_c(state) { return state.abc_graph_data_c; },
    item_mark_a(state) { return state.item_mark_a; },
    item_mark_b(state) { return state.item_mark_b; },
    item_mark_c(state) { return state.item_mark_c; },
    ClassProductObject(state) {
      return [{
        'info': state.class_a_products,
        'resume': state.class_a_resume,
        'title': 'Curva A - 20% dos produtos',
        'bg': 'gb',
        'bullet': '\u25CF',
        'color': '#7ABA1E',
        'img_color': 'filter: invert(82%) sepia(17%) saturate(7028%) hue-rotate(36deg) brightness(91%) contrast(76%);'
          // https://codepen.io/sosuke/pen/Pjoqqp
      }, {
        'info': state.class_b_products,
        'resume': state.class_b_resume,
        'title': 'Curva B - 30% dos produtos',
        'bg': 'bb',
        'bullet': '\u25C6',
        'color': '#0BA7DA',
        'img_color': 'filter: invert(45%) sepia(85%) saturate(1686%) hue-rotate(162deg) brightness(98%) contrast(91%)'
          // https://codepen.io/sosuke/pen/Pjoqqp
      }, {
        'info': state.class_c_products,
        'resume': state.class_c_resume,
        'title': 'Curva C - 50% dos produtos',
        'bg': 'yb',
        'bullet': '\u25A0',
        'color': '#FCBC2C',
        'img_color': 'filter: invert(91%) sepia(17%) saturate(7273%) hue-rotate(336deg) brightness(106%) contrast(98%)'
          // https://codepen.io/sosuke/pen/Pjoqqp
      }];
    },
    // shipping costs context ======================================================
    shipping_costs_context_loaded(state) { return state.shipping_costs_context_loaded },
    ShippingCostsObject(state) {
      return [{
        'info': state.shipping_initial_costs,
        'title': 'Fretes por valores de cotação',
        'bg': 'gb',
        'color': '#7ABA1E',
        'img_color': 'filter: invert(82%) sepia(17%) saturate(7028%) hue-rotate(36deg) brightness(91%) contrast(76%);'
      }, {
        'info': state.shipping_final_costs,
        'title': 'Fretes por valores cobrados',
        'bg': 'bb',
        'color': '#0BA7DA',
        'img_color': 'filter: invert(45%) sepia(85%) saturate(1686%) hue-rotate(162deg) brightness(98%) contrast(91%)'
      }, {
        'info': state.shipping_difference_costs,
        'title': 'Fretes com maior diferença entre frete cotado e frete cobrado',
        'bg': 'yb',
        'color': '#FCBC2C',
        'img_color': 'filter: invert(91%) sepia(17%) saturate(7273%) hue-rotate(336deg) brightness(106%) contrast(98%)'
      }]
    },
    // funnel sales context ========================================================
    funnel_sales_context_loaded(state) { return state.funnel_sales_context_loaded },
    funnelData(state) { return state.funnelData },
    conversionSteps(state) { return state.conversionSteps },
    checkoutSubSteps(state) { return state.checkoutSubSteps },
    funnelOriginSelectedIndex(state) { return state.funnelOriginSelectedIndex },
    funnelOriginSelectedCode(state) { return state.funnelOriginSelectedCode },
    funnelPeriodSelectedIndex(state) { return state.funnelPeriodSelectedIndex },
    funnelPeriodSelectedCode(state) { return state.funnelPeriodSelectedCode },
    funnelPeriodDateStart(state) { return state.funnelPeriodDateStart },
    funnelPeriodDateEnd(state) { return state.funnelPeriodDateEnd },
    // Dados adicionais do funil
    funnelAnalyticsVisits(state) { return state.funnelAnalyticsVisits },
    totalVisits(state) { return state.totalVisits },
    totalCarts(state) { return state.totalCarts },
    totalCheckout(state) { return state.totalCheckout },
    totalConverted(state) { return state.totalConverted },
    totalAbandoned(state) { return state.totalAbandoned },
    conversionRate(state) { return state.conversionRate },
    abandonmentRateValue(state) { return state.abandonmentRateValue },
    averageTicket(state) { return state.averageTicket },
    totalRevenue(state) { return state.totalRevenue },
    cartTimeline(state) { return state.cartTimeline },
    // Dados específicos do funil detalhado
    funilUsuarios(state) { return state.funilUsuarios },
    funilCarrinhos(state) { return state.funilCarrinhos },
    funilCheckout(state) { return state.funilCheckout },
    funilLogin(state) { return state.funilLogin },
    funilFrete(state) { return state.funilFrete },
    funilPagamento(state) { return state.funilPagamento },
    funilPedidos(state) { return state.funilPedidos },
    // live cart context ===========================================================
    live_cart_context_loaded(state) { return state.live_cart_context_loaded },
    activeCarts(state) { return state.activeCarts },
    activeCartsCount(state) { return state.activeCarts.length },
    realTimeUpdates(state) { return state.realTimeUpdates },
    abandonedCartData(state) { return state.abandonedCartData },
    abandonmentRate(state) { return state.abandonmentRate },
    averageAbandonmentTime(state) { return state.averageAbandonmentTime },
    lostValue(state) { return state.lostValue },
    recoverableValue(state) { return state.recoverableValue },
    liveCartOriginSelectedIndex(state) { return state.liveCartOriginSelectedIndex },
    liveCartOriginSelectedCode(state) { return state.liveCartOriginSelectedCode },
    liveCartPeriodSelectedIndex(state) { return state.liveCartPeriodSelectedIndex },
    liveCartPeriodSelectedCode(state) { return state.liveCartPeriodSelectedCode },
    liveCartPeriodDateStart(state) { return state.liveCartPeriodDateStart },
    liveCartPeriodDateEnd(state) { return state.liveCartPeriodDateEnd },
    LiveCartMetricsObject() {
      return [{
        'info': '25',
        'title': 'carrinhos ativos',
        'bg': 'gb',
        'show_diff': true,
        'diff': 25,
        'diff_qtde': 5,
        'img': 'period/cart_icon.svg'
      }, {
        'info': 'R$ 12.450',
        'title': 'valor em carrinhos',
        'bg': 'bb',
        'show_diff': true,
        'diff': 18,
        'diff_qtde': 1890,
        'img': 'ticket.svg'
      }, {
        'info': '68%',
        'title': 'taxa de abandono',
        'bg': 'rb',
        'show_diff': true,
        'diff': -5,
        'diff_qtde': -3,
        'img': 'period/orders.svg'
      }, {
        'info': 'R$ 8.200',
        'title': 'valor recuperável',
        'bg': 'yb',
        'show_diff': true,
        'diff': 12,
        'diff_qtde': 890,
        'img': 'period/total_bucket_card.svg'
      }]
    },
    // customer behavior context ===========================================================
    customer_behavior_context_loaded(state) { return state.customer_behavior_context_loaded },
    topProductsAdded(state) { return state.topProductsAdded },
    customerBehaviorPeriodSelectedIndex(state) { return state.customerBehaviorPeriodSelectedIndex },
    customerBehaviorOriginSelectedIndex(state) { return state.customerBehaviorOriginSelectedIndex },
    customerBehaviorPeriodSelectedCode(state) { return state.customerBehaviorPeriodSelectedCode },
    customerBehaviorOriginSelectedCode(state) { return state.customerBehaviorOriginSelectedCode },
    customerBehaviorPeriodDateStart(state) { return state.customerBehaviorPeriodDateStart },
    customerBehaviorPeriodDateEnd(state) { return state.customerBehaviorPeriodDateEnd },
    // sales map getters ======================================================
    sales_map_context_loaded(state) { return state.sales_map_context_loaded },
    sales_map_title(state) { return state.sales_map_title },
    sales_map_total_orders(state) { return state.sales_map_total_orders },
    sales_map_applied_filters(state) { return state.sales_map_applied_filters },
    sales_map_regions(state) { return state.sales_map_regions },
    sales_map_customer_profile(state) { return state.sales_map_customer_profile },
    sales_map_top_shipping_services(state) { return state.sales_map_top_shipping_services },
    sales_map_top_shipping_title(state) { return state.sales_map_top_shipping_title },
    sales_map_top_products(state) { return state.sales_map_top_products },
    sales_map_top_products_title(state) { return state.sales_map_top_products_title },
    sales_map_top_cities(state) { return state.sales_map_top_cities },
    sales_map_top_cities_title(state) { return state.sales_map_top_cities_title },
    sales_map_top_states(state) { return state.sales_map_top_states },
    sales_map_top_states_title(state) { return state.sales_map_top_states_title },
    sales_map_state_values(state) { return state.sales_map_state_values },
    sales_map_svg_content(state) { return state.sales_map_svg_content },
    sales_map_states_details(state) { return state.sales_map_states_details },
    CustomerBehaviorMetricsObject(state) {
      const formatPercentageDisplay = (value) => {
        if (value === null || value === undefined || value === '') {
          return formatPercentage(0);
        }

        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.endsWith('%')) {
            return trimmed;
          }

          return formatPercentage(trimmed);
        }

        return formatPercentage(value);
      };

      return [
        {
          'info': formatPercentageDisplay(state.desktop_percentage),
          'title': 'desktop',
          'bg': 'bb',
          'show_diff': false,
          'img': 'period/desktop_v2.svg'
        },
        {
          'info': formatPercentageDisplay(state.mobile_percentage),
          'title': 'mobile',
          'bg': 'gb',
          'show_diff': false,
          'img': 'period/mobile_v2.svg'
        },
        {
          'info': formatCurrencyBRL(state.averageTicket),
          'title': 'ticket médio',
          'bg': 'yb',
          'show_diff': false,
          'img': 'ticket.svg'
        },
        {
          'info': formatCurrencyBRL(state.totalRevenue),
          'title': 'receita total',
          'bg': 'rb',
          'show_diff': false,
          'img': 'period/wallet_v2.svg'
        }
      ]
    }
  },
  actions: {
    async fetchAnalyticsData() {
      try {
        const response = await ApiService.get('analytics/');
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar dados de analytics:', error);
        throw error;
      }
    },
    async requestFunnelSalesData({ commit }, payload) {
      try {
        // Fazer requisição ao endpoint do funil com os filtros
        const url = 'order/dashboard/funnel_sales/';
        const params = {
          'period': payload.period,
          'site_or_order_origin': payload.site_or_order_origin
        };
        
        // Adicionar datas se for intervalo personalizado
        if (payload.period === 8) {
          params.date_start = payload.date_start;
          params.date_end = payload.date_end;
        }
        
        const response = await ApiService.get(url, { params });
        const funnelData = response.data;
        
        commit('updateFunnelSalesData', funnelData);
        return funnelData;
      } catch (error) {
        console.error('Erro ao buscar dados do funil:', error);
        throw error;
      }
    },
    async requestCustomerBehaviorData({ commit, state }, payload = {}) {
      try {
        const previousDesktopPercentage = state.desktop_percentage;
        const previousMobilePercentage = state.mobile_percentage;
        const hasInitialDeviceData = hasPositivePercentageValue(previousDesktopPercentage) || hasPositivePercentageValue(previousMobilePercentage);
        const skipOrderContextRefresh = payload.skipOrderContextRefresh === true;

        if (hasInitialDeviceData) {
          if (!state.customer_behavior_context_loaded) {
            commit('setCustomerBehaviorContextLoaded', true);
          }
        } else {
          commit('setCustomerBehaviorContextLoaded', false);
        }

        const period = payload.period != null ? payload.period : state.customerBehaviorPeriodSelectedIndex;

        const resolveOriginParam = () => {
          if (payload.site_or_order_origin !== undefined && payload.site_or_order_origin !== null) {
            return payload.site_or_order_origin;
          }

          const availableOrigins = Array.isArray(state.site_or_order_origin) ? state.site_or_order_origin : [];

          if (availableOrigins.length > 0) {
            const index = state.customerBehaviorOriginSelectedIndex;
            if (index !== undefined && index !== null && availableOrigins[index]) {
              const optionByIndex = availableOrigins[index];
              if (optionByIndex.site_or_origin !== undefined && optionByIndex.site_or_origin !== null) {
                return optionByIndex.site_or_origin;
              }
              if (optionByIndex.site_or_order_origin !== undefined && optionByIndex.site_or_order_origin !== null) {
                return optionByIndex.site_or_order_origin;
              }
            }

            const code = state.customerBehaviorOriginSelectedCode;
            const optionByCode = availableOrigins.find(option => option && option.code === code);
            if (optionByCode) {
              if (optionByCode.site_or_origin !== undefined && optionByCode.site_or_origin !== null) {
                return optionByCode.site_or_origin;
              }
              if (optionByCode.site_or_order_origin !== undefined && optionByCode.site_or_order_origin !== null) {
                return optionByCode.site_or_order_origin;
              }
              if (optionByCode.code !== undefined && optionByCode.code !== null) {
                return optionByCode.code;
              }
            }
          }

          return state.customerBehaviorOriginSelectedCode != null
            ? state.customerBehaviorOriginSelectedCode
            : null;
        };

        const origin = resolveOriginParam();

        const timestamp = new Date().getTime();
        let normalizedDateStart = null;
        let normalizedDateEnd = null;

        if (period === 8) {
          normalizedDateStart = payload.date_start != null ? payload.date_start : state.customerBehaviorPeriodDateStart;
          normalizedDateEnd = payload.date_end != null ? payload.date_end : state.customerBehaviorPeriodDateEnd;
        }

        const buildOrderContextPayload = () => {
          const retryPayload = {
            period
          };

          if (origin !== null && origin !== undefined && origin !== '') {
            retryPayload.site_or_order_origin = origin;
          }

          if (period === 8) {
            if (normalizedDateStart) {
              retryPayload.date_start = normalizedDateStart;
            }

            if (normalizedDateEnd) {
              retryPayload.date_end = normalizedDateEnd;
            }
          }

          return retryPayload;
        };

        const buildBaseParams = () => {
          const baseParams = {
            period,
            t: timestamp
          };

          if (origin !== null && origin !== undefined && origin !== '') {
            baseParams.site_or_order_origin = origin;
          }

          if (period === 8) {
            if (normalizedDateStart) {
              baseParams.date_start = normalizedDateStart;
            }

            if (normalizedDateEnd) {
              baseParams.date_end = normalizedDateEnd;
            }
          }

          return baseParams;
        };

        const customerBehaviorParams = buildBaseParams();
        const customerBehaviorRequest = ApiService.get('order/dashboard/customer_behavior/', { params: customerBehaviorParams });

        const shouldFetchAnalytics = payload.fetchAnalytics === true;
        let analyticsTotalVisits = null;

        let analyticsDateStart = null;
        let analyticsDateEnd = null;

        if (period === 8) {
          analyticsDateStart = normalizedDateStart != null ? normalizedDateStart : state.customerBehaviorPeriodDateStart;
          analyticsDateEnd = normalizedDateEnd != null ? normalizedDateEnd : state.customerBehaviorPeriodDateEnd;
        }

        if (shouldFetchAnalytics) {
          const analyticsParams = {
            t: new Date().getTime(),
            period,
            values: {
              site_id: state.site_id,
              current_total_orders_value_count: state.current_total_orders_value_count,
              current_paid_orders_count: state.current_paid_orders_count,
              past_paid_orders_count: state.past_paid_orders_count,
              past_total_orders_value_count: state.past_total_orders_value_count,
            },
          };

          const normalizedDateStart = analyticsDateStart != null ? analyticsDateStart : state.customerBehaviorPeriodDateStart;
          const normalizedDateEnd = analyticsDateEnd != null ? analyticsDateEnd : state.customerBehaviorPeriodDateEnd;

          if (normalizedDateStart !== undefined) {
            analyticsParams.date_start = normalizedDateStart;
          }
          if (normalizedDateEnd !== undefined) {
            analyticsParams.date_end = normalizedDateEnd;
          }

          try {
            const analyticsResponse = await ApiService.get('order/dashboard/order_request_remaining_dashboard_analytics/', {
              params: analyticsParams
            });
            const analyticsData = analyticsResponse.data || {};
            commit('setAnalyticsData', analyticsData);
            analyticsTotalVisits = coalesce(analyticsData.total_visits, analyticsData.total_visitas, null);
          } catch (error) {
            console.error('Erro ao buscar analytics para comportamento do cliente:', error);
          }
        }

        const response = await customerBehaviorRequest;
        const responseData = response.data || {};

        const fallbackTotalVisits = analyticsTotalVisits != null
          ? analyticsTotalVisits
          : coalesce(state.total_visits, state.total_visitas, null);

        responseData.total_visits = fallbackTotalVisits;
        responseData.total_visitas = fallbackTotalVisits;
        responseData.customer_behavior_total_visits = fallbackTotalVisits;

        commit('updateCustomerBehaviorData', responseData);
        commit('setTopProductsAdded', responseData.produtos_mais_adicionados || []);

        const currentDesktopPercentage = state.desktop_percentage;
        const currentMobilePercentage = state.mobile_percentage;
        const desktopIsZero = isZeroPercentageValue(currentDesktopPercentage);
        const mobileIsZero = isZeroPercentageValue(currentMobilePercentage);

        if (desktopIsZero && hasPositivePercentageValue(previousDesktopPercentage)) {
          commit('setDesktopPercentage', previousDesktopPercentage);
        }

        if (mobileIsZero && hasPositivePercentageValue(previousMobilePercentage)) {
          commit('setMobilePercentage', previousMobilePercentage);
        }

        if (!skipOrderContextRefresh && (desktopIsZero || mobileIsZero)) {
          commit('updateOrderPeriodContext', buildOrderContextPayload());
        }

        commit('setCustomerBehaviorContextLoaded', true);
        return responseData;
      } catch (error) {
        console.error('Erro ao buscar dados de comportamento do cliente:', error);
        commit('setTopProductsAdded', []);
        commit('setCustomerBehaviorContextLoaded', true);
        throw error;
      }
    },
  },
  mutations: {
    // components comm ====================================================
    updatePeriodOriginSelectedIndex(state, payload) {
      state.orderPeriodSelectedIndex = payload.period;
      state.orderOriginSelectedIndex = payload.origin;
      state.orderPeriodDateStart = payload.date_start;
      state.orderPeriodDateEnd = payload.date_end;
    },
    updateSalesProductsSiteOriginSelectedIndex(state, payload) { state.salesProductsSiteOriginSelectedIndex = payload; },
    updateStockSiteOriginSelectedIndex(state, payload) { state.stockSiteOriginSelectedIndex = payload; },
    updateOrderPeriodSelectedCode(state, payload) { state.orderPeriodSelectedCode = payload; },
    updateOriginSelectedCode(state, payload) { state.orderOriginSelectedCode = payload; },
    updateStockOriginSelectedCode(state, payload) { state.stockOriginSelectedCode = payload; },
    updateSalesProductsOriginSelectedCode(state, payload) { state.salesProductsOriginSelectedCode = payload; },
    updateAbcPeriodSelectedCode(state, payload) { state.orderPeriodSelectedCode = payload; },
    updateAbcAdminPeriod(state, payload) { state.abcAdminPeriod = payload; },
    // load on demand =====================================================
    requestPeriodContext(state) {
      toogleRequesting(state);
      getContext('site/dashboard/site/').then(siteResponse => {
        Object.keys(siteResponse).forEach(index => {
          state[index] = siteResponse[index];
        });
      }).catch(err => {
        console.log(err);
      });
      getContext(
        'order/dashboard/order_period_context/', { 'period': state.orderPeriodSelectedIndex, 'first_request': true }
      ).then(orderResponse => {
        getContext(
          'customer/dashboard/customer/', {
            'period': state.orderPeriodSelectedIndex,
          }
        ).then(customerResponse => {
          Object.keys(orderResponse).forEach(index => {
            state[index] = orderResponse[index];
          });
          Object.keys(customerResponse).forEach(index => {
            state[index] = customerResponse[index];
          });
          toogleRequesting(state);
          if ((!state.is_multi_site_manager && !state.is_marketplace) || (state.is_multi_site_manager && !state.is_marketplace && state.site_id)) {
            getContext(
              'order/dashboard/order_request_remaining_dashboard_analytics/', {
                't': new Date().getTime(),
                'period': state.orderPeriodSelectedIndex,
                'values': {
                  'site_id': state.site_id,
                  'current_total_orders_value_count': state.current_total_orders_value_count,
                  'current_paid_orders_count': state.current_paid_orders_count,
                  'past_paid_orders_count': state.past_paid_orders_count,
                  'past_total_orders_value_count': state.past_total_orders_value_count,
                },
                'date_start': state.orderPeriodDateStart,
                'date_end': state.orderPeriodDateEnd,
              }
            ).then(analyticsResponse => {
              Object.keys(analyticsResponse).forEach(index => {
                state[index] = analyticsResponse[index];
              });
            }).catch(err => {
              console.log(err);
            });
          }
        }).catch(err => {
          console.log(err);
        });
      }).catch(err => {
        console.log(err);
      });
    },
    requestCommonContext(state, obj) {
      toogleRequesting(state);
      getContext(obj.url, { 'period': obj.period }).then(response => {
        // Verificar se é o endpoint do funil de vendas
        if (obj.url === 'order/dashboard/funnel_sales/') {
          // Usar a mutation específica para mapear os dados do funil
          this.commit('updateFunnelSalesContext', response);
        } else if (obj.url && obj.url.includes('national-map')) {
          // Usar a mutation específica para mapear os dados do mapa nacional
          this.commit('updateSalesMapContext', response);
        } else {
          // Usar o método genérico para outros endpoints
          Object.keys(response).forEach(index => {
            state[index] = response[index];
          });
        }
      }).catch(err => {
        console.log(err);
      });
    },
    // filters api requests ===============================================
    updateOrderPeriodContext(state, payload) {
      let custom_payload = payload || {};
      custom_payload.t = new Date().getTime();
      new Promise(() => {
        ApiService.get('order/dashboard/order_period_context/', {
          params: custom_payload,
        }).then(orderResponse => {
          new Promise(() => {
            ApiService.get('customer/dashboard/customer/', {
              params: custom_payload,
            }).then(customerResponse => {
              Object.keys(orderResponse.data).forEach(index => {
                state[index] = orderResponse.data[index];
              });
              Object.keys(customerResponse.data).forEach(index => {
                state[index] = customerResponse.data[index];
              });
              if ((!state.is_multi_site_manager && !state.is_marketplace) || (state.is_multi_site_manager && !state.is_marketplace && state.site_id)) {
                new Promise(() => {
                  ApiService.get('order/dashboard/order_request_remaining_dashboard_analytics/', {
                    params: {
                      't': new Date().getTime(),
                      'period': this.state.orderPeriodSelectedIndex,
                      'values': {
                        'site_id': this.state.site_id,
                        'current_total_orders_value_count': this.state.current_total_orders_value_count,
                        'current_paid_orders_count': this.state.current_paid_orders_count,
                        'past_paid_orders_count': this.state.past_paid_orders_count,
                        'past_total_orders_value_count': this.state.past_total_orders_value_count,
                      },
                      'date_start': this.state.orderPeriodDateStart,
                      'date_end': this.state.orderPeriodDateEnd,
                    }
                  }).then(analyticsResponse => {
                    Object.keys(analyticsResponse.data).forEach(index => {
                      state[index] = analyticsResponse.data[index];
                    });
                  }).catch(err => {
                    console.log(err);
                  });
                });
              }
            }).catch(err => {
              console.log(err);
            });
          });
        }).catch(err => {
          console.log(err);
        });
      });
    },
    updateCommonContext(state, obj) {
      new Promise(() => {
        let custom_payload = obj.payload || {};
        custom_payload.t = new Date().getTime();
        ApiService.get(obj.url, { params: custom_payload }).then(response => {
          Object.keys(response.data).forEach(index => {
            state[index] = response.data[index];
          });
        }).catch(err => {
          console.log(err);
        });
      });
    },
    updateFunnelPeriodOriginSelectedIndex(state, payload) {
      state.funnelPeriodSelectedIndex = payload.period;
      state.funnelOriginSelectedIndex = payload.origin;
      state.funnelPeriodDateStart = payload.date_start;
      state.funnelPeriodDateEnd = payload.date_end;
    },
    updateFunnelPeriodSelectedCode(state, payload) { state.funnelPeriodSelectedCode = payload; },
    updateFunnelOriginSelectedCode(state, payload) { state.funnelOriginSelectedCode = payload; },
    updateLiveCartPeriodOriginSelectedIndex(state, payload) {
      state.liveCartPeriodSelectedIndex = payload.period;
      state.liveCartOriginSelectedIndex = payload.origin;
      state.liveCartPeriodDateStart = payload.date_start;
      state.liveCartPeriodDateEnd = payload.date_end;
    },
    updateLiveCartPeriodSelectedCode(state, payload) { state.liveCartPeriodSelectedCode = payload; },
    updateLiveCartOriginSelectedCode(state, payload) { state.liveCartOriginSelectedCode = payload; },
    // customer behavior context ===========================================================
    updateCustomerBehaviorPeriodOriginSelectedIndex(state, payload) {
      state.customerBehaviorPeriodSelectedIndex = payload.period;
      state.customerBehaviorOriginSelectedIndex = payload.origin;
      state.customerBehaviorPeriodDateStart = payload.date_start;
      state.customerBehaviorPeriodDateEnd = payload.date_end;
    },
    updateCustomerBehaviorPeriodSelectedCode(state, payload) { state.customerBehaviorPeriodSelectedCode = payload; },
    updateCustomerBehaviorOriginSelectedCode(state, payload) { state.customerBehaviorOriginSelectedCode = payload; },
    updateCustomerBehaviorContext(state, obj) {
      // Esta mutation será chamada pela action requestCustomerBehaviorData
      // Mantém apenas para compatibilidade
      this.dispatch('requestCustomerBehaviorData', {
        'period': obj.period || state.customerBehaviorPeriodSelectedIndex,
        'site_or_order_origin': obj.site_or_order_origin || state.customerBehaviorOriginSelectedCode,
        'date_start': obj.date_start || state.customerBehaviorPeriodDateStart,
        'date_end': obj.date_end || state.customerBehaviorPeriodDateEnd,
        'fetchAnalytics': obj.fetchAnalytics !== undefined ? obj.fetchAnalytics : true,
        'skipOrderContextRefresh': obj.skipOrderContextRefresh === true
      });
    },
    resetFunnelSalesContext(state) {
      // Resetar completamente o estado do funil
      console.log('[FUNIL] Resetando estado do funil');
      state.funnel_sales_context_loaded = false;
      state.funnelAnalyticsVisits = null;
      state.funnelData = [];
      state.conversionSteps = [];
      state.checkoutSubSteps = [];
    },
    updateFunnelSalesContext(state, payload) {
      // LIMPAR visitantes do funil ANTES de qualquer request
      // Garante que não use valores antigos da dashboard
      state.funnelAnalyticsVisits = null;
      
      const requestPayload = {
        'period': payload.period,
        'site_or_order_origin': payload.site_or_order_origin
      };
      
      // Adicionar datas se for intervalo personalizado
      if (payload.period === 8) {
        requestPayload.date_start = payload.date_start;
        requestPayload.date_end = payload.date_end;
      }
      
      console.log('[FUNIL] Iniciando busca de visitantes - Período:', payload.period);
      
      // REQUEST DIRETO AO GOOGLE ANALYTICS (totalmente independente)
      if ((!state.is_multi_site_manager && !state.is_marketplace) || (state.is_multi_site_manager && !state.is_marketplace && state.site_id)) {
        // 1. Buscar contexto de pedidos com período DO FUNIL
        ApiService.get('order/dashboard/order_period_context/', {
          params: {
            'period': payload.period,  // Período do FUNIL
            't': new Date().getTime(),
          }
        }).then(orderContextResponse => {
          const orderData = orderContextResponse.data;
          
          console.log('[FUNIL] Contexto de pedidos recebido, buscando analytics...');
          
          // 2. Buscar analytics com valores DO FUNIL (não da dashboard)
          return ApiService.get('order/dashboard/order_request_remaining_dashboard_analytics/', {
            params: {
              't': new Date().getTime(),
              'period': payload.period,  // MESMO período
              'values': {
                'site_id': state.site_id,
                'current_total_orders_value_count': orderData.current_total_orders_value_count,
                'current_paid_orders_count': orderData.current_paid_orders_count,
                'past_paid_orders_count': orderData.past_paid_orders_count,
                'past_total_orders_value_count': orderData.past_total_orders_value_count,
              },
              'date_start': payload.date_start,
              'date_end': payload.date_end,
            }
          });
        }).then(analyticsResponse => {
          const analyticsData = analyticsResponse.data;
          
          // 3. EXTRAIR visitantes APENAS do analytics
          const analyticsVisits = coalesce(
            analyticsData.total_visits,
            analyticsData.total_visitas,
            null
          );
          
          console.log('[FUNIL] Analytics recebido - Visitantes:', analyticsVisits);
          
          // 4. SALVAR visitantes (AGUARDAR API antes de processar)
          state.funnelAnalyticsVisits = analyticsVisits;
          
          // 5. SOMENTE AGORA buscar dados do funil
          console.log('[FUNIL] Buscando dados do funil com visitantes:', analyticsVisits);
          this.dispatch('requestFunnelSalesData', requestPayload);
        }).catch(err => {
          console.error('[FUNIL] Erro ao buscar analytics:', err);
          // Se falhar, não buscar funil (evita mostrar dados incorretos)
          state.funnelAnalyticsVisits = null;
          // Mesmo com erro, buscar funil (mostrará 0 visitantes)
          this.dispatch('requestFunnelSalesData', requestPayload);
        });
      } else {
        // Marketplace: sem analytics
        console.log('[FUNIL] Marketplace - sem analytics');
        state.funnelAnalyticsVisits = null;
        this.dispatch('requestFunnelSalesData', requestPayload);
      }
    },
    updateFunnelSalesData(state, data) {
      console.log('[FUNIL] Processando dados do funil com visitantes:', state.funnelAnalyticsVisits);
      mapFunnelLikeResponseToState(state, data, true);  // true = contexto do funil
      state.funnel_sales_context_loaded = true;
    },
    updateCustomerBehaviorData(state, data) {
      mapFunnelLikeResponseToState(state, data, false);  // false = comportamento do cliente
    },
    setAnalyticsData(state, data) {
      Object.keys(data).forEach(key => {
        state[key] = data[key];
      });
      if (Object.prototype.hasOwnProperty.call(data, 'analytics_loaded')) {
        state.analytics_loaded = data.analytics_loaded;
      }
    },
    updateSalesMapContext(state, payload) {
      // Mapear dados do payload do backend para o state
      state.sales_map_title = payload.title || '';
      state.sales_map_total_orders = payload.total_orders || 0;
      
      // Converter applied_filters de objeto para array
      state.sales_map_applied_filters = [];
      if (payload.applied_filters) {
        Object.keys(payload.applied_filters).forEach(key => {
          state.sales_map_applied_filters.push({
            name: key,
            value: payload.applied_filters[key]
          });
        });
      }
      
      // Regions já vem no formato correto
      state.sales_map_regions = payload.regions || [];
      
      // Converter states para state_values (manter valor EXATO da API, SEM conversão)
      state.sales_map_state_values = {};
      state.sales_map_states_details = {};
      if (payload.states) {
        Object.keys(payload.states).forEach(stateCode => {
          const stateData = payload.states[stateCode];
          
          // Manter o valor EXATAMENTE como vem da API - SEM QUALQUER PROCESSAMENTO
          if (stateData.has_data === true && stateData.value !== null && stateData.value !== undefined && stateData.value !== '') {
            // Usar o valor diretamente, seja número ou string
            state.sales_map_state_values[stateCode] = stateData.value;
          } else {
            state.sales_map_state_values[stateCode] = 0;
          }
          // Detalhes por estado (top lists) quando disponíveis
          const details = {};
          if (Array.isArray(stateData.top_shippings)) {
            details.top_shippings = stateData.top_shippings.map(item => ({
              name: item.name || item.label || '',
              quantity: parseIntegerValue(coalesce(item.count, item.quantity, item.total, 0))
            }));
          }
          if (Array.isArray(stateData.top_products)) {
            details.top_products = stateData.top_products.map(item => ({
              name: coalesce(item.name, item.label, item.product_name, 'Produto'),
              quantity: parseIntegerValue(coalesce(item.count, item.quantity, item.total, 0))
            }));
          }
          if (Array.isArray(stateData.top_cities)) {
            details.top_cities = stateData.top_cities.map(item => ({
              name: coalesce(item.name, item.city, 'Cidade'),
              quantity: parseIntegerValue(coalesce(item.count, item.quantity, item.total, 0))
            }));
          }
          if (stateData.customer_profile) {
            details.customer_profile = stateData.customer_profile;
          }
          state.sales_map_states_details[stateCode] = details;
        });
      }
      
      // Converter customer_profile
      if (payload.customer_profile) {
        const gender = payload.customer_profile.gender || {};
        const male = gender.male || {};
        const female = gender.female || {};
        state.sales_map_customer_profile = {
          males: {
            quantity: male.quantity || 0,
            percentage: parseFloat((male.percentage || '0').replace('%', '')) || 0
          },
          females: {
            quantity: female.quantity || 0,
            percentage: parseFloat((female.percentage || '0').replace('%', '')) || 0
          },
          ageGroups: (payload.customer_profile.age_groups || []).map(ageGroup => {
            const ageMale = ageGroup.male || {};
            const ageFemale = ageGroup.female || {};
            return {
              label: ageGroup.range || '',
              male: {
                value: ageMale.quantity || 0,
                percentage: parseFloat((ageMale.percentage || '0')) || 0,
                rotate: false
              },
              female: {
                value: ageFemale.quantity || 0,
                percentage: parseFloat((ageFemale.percentage || '0')) || 0,
                rotate: false
              }
            };
          })
        };
      }
      
      // Top shippings
      state.sales_map_top_shipping_services = (payload.top_shippings || []).map(shipping => ({
        name: shipping.name || '',
        quantity: shipping.count || 0
      }));
      
      // Top products (formato: name e count)
      state.sales_map_top_products = (payload.top_products || []).map(product => ({
        name: product.name || '',
        count: product.count || 0
      }));
      
      // Top cities
      state.sales_map_top_cities = (payload.top_cities || []).map(city => ({
        name: city.name || '',
        quantity: city.count || 0
      }));
      
      // Top states (formato: name e count)
      state.sales_map_top_states = (payload.top_states || []).map(stateItem => ({
        name: stateItem.name || '',
        count: stateItem.count || 0
      }));
      
      state.sales_map_context_loaded = true;
    },
    setFunnelSalesData(state, payload) {
      Object.keys(payload).forEach(key => {
        state[key] = payload[key];
      });
    },
    updateLiveCartContext() {
      // Mock implementation for testing
    },
    setTopProductsAdded(state, products) {
      state.topProductsAdded = normalizeTopProducts(products);
    },
    setCustomerBehaviorContextLoaded(state, loaded) {
      state.customer_behavior_context_loaded = loaded;
    },
    setDesktopPercentage(state, value) {
      state.desktop_percentage = value;
    },
    setMobilePercentage(state, value) {
      state.mobile_percentage = value;
    }
  }
});